/**
 * Transforma os eventos do motor em frases (a faixa de notícias e o histórico)
 * e em "momentos" grandes (cartas, facadas, falências...) para a tela.
 */
import { RESORT_SQUARES, type GameEvent } from '@tigrinho/engine';
import { CARD_TEXT, HELD_TEXT, TENSION } from './content';

export interface DescribeCtx {
  name: (id: string) => string;
  square: (i: number) => string;
  money: (n: number) => string;
  you: string | null;
}

export type Tone = 'neutral' | 'good' | 'bad' | 'hot';

export interface Line {
  text: string;
  tone: Tone;
}

export type Moment =
  | { kind: 'card'; player: string; card: string; clover?: boolean }
  | { kind: 'facada'; payer: string; owner: string; amount: number; square: number; mortal: boolean }
  | { kind: 'group'; player: string; group: number }
  | { kind: 'hotel'; player: string; square: number }
  | { kind: 'bankrupt'; player: string }
  | { kind: 'takeover'; buyer: string; from: string; square: number; price: number }
  | { kind: 'destroyed'; owner: string; square: number; by: string | null }
  | { kind: 'banner'; title: string; line: string; tone: Tone }
  | { kind: 'shield'; target: string }
  /** Marco de riqueza (só na tela): 2, 5 ou 10 milhões de contos em caixa. */
  | { kind: 'milestone'; player: string; millions: number };

const reasonText: Record<string, string> = {
  aluguel: 'de aluguel',
  imposto: 'de imposto',
  cadeia: 'para sair da Ilha',
  viagem: 'pela viagem no Disco Voador',
  construcao: 'em construção',
};

const who = (c: DescribeCtx, id: string) => (id === c.you ? 'Você' : c.name(id));

/** "só colocou a bandeirola", "construiu a casa 2", "ergueu um hotel". */
export function builtPhrase(from: number, level: number): string {
  if (level === 0) return 'só colocou a bandeirola em';
  if (level === 4) return 'ergueu um HOTEL em';
  if (from < 0) return `comprou e já subiu a casa ${level} em`;
  return `construiu a casa ${level} em`;
}

export function describe(e: GameEvent, c: DescribeCtx): Line | null {
  const m = c.money;
  switch (e.type) {
    case 'gameStarted':
      return { text: 'A partida começou. Boa sorte (vai precisar).', tone: 'hot' };
    case 'diceRolled':
      return { text: `${who(c, e.player)} tirou ${e.roll.dice.join(' e ')}${e.roll.doubles ? ' (dupla!)' : ''}`, tone: 'neutral' };
    case 'cardBought':
      return { text: `${who(c, e.player)} comprou ${HELD_TEXT[e.card] ?? e.card} na lojinha`, tone: 'neutral' };
    case 'taxExempt':
      return { text: `O Contador livrou ${who(c, e.player)} do Faz o L (${m(e.amount)})`, tone: 'good' };
    case 'taxEvasion':
      return e.success
        ? { text: `${who(c, e.player)} sonegou (${e.total} nos dados) e pagou só ${m(e.amount)}`, tone: 'good' }
        : { text: `${who(c, e.player)} foi pego sonegando (${e.total}): pagou ${m(e.amount)} e foi preso`, tone: 'bad' };
    case 'mstArrived':
      return { text: `O MST chegou em ${c.square(e.square)}, de ${c.name(e.owner)}`, tone: 'hot' };
    case 'mstInvaded':
      return { text: `O MST invadiu ${c.square(e.square)}: ${e.from === 1 ? 'sobrou a bandeirola' : `voltou para a casa ${e.to}`}`, tone: 'bad' };
    case 'mstExpelled':
      return { text: `O fazendeiro de ${c.name(e.owner)} mandou o MST embora de ${c.square(e.square)}`, tone: 'good' };
    case 'farmProtected':
      return { text: `${who(c, e.player)} protegeu ${c.square(e.square)} com a Carta Fazendeiro`, tone: 'good' };
    case 'magnetRolled':
      return e.pulled
        ? { text: `Ímã do Azarão! ${who(c, e.player)} foi puxado para ${c.square(e.square)}`, tone: 'bad' }
        : { text: `${who(c, e.player)} escapou do ímã de ${c.name(e.owner)}`, tone: 'neutral' };
    case 'passedStart':
      return { text: `${who(c, e.player)} passou pelo ${c.square(0)}: +${m(e.amount)}`, tone: 'good' };
    case 'sentToJail':
      return { text: `${who(c, e.player)} ficou preso na ${c.square(8)}`, tone: 'bad' };
    case 'leftJail':
      return { text: `${who(c, e.player)} saiu da ${c.square(8)}`, tone: 'neutral' };
    case 'jailRollFailed':
      return { text: `${who(c, e.player)} não tirou dupla (${e.attempts} de 3)`, tone: 'neutral' };
    case 'built':
      if (RESORT_SQUARES.includes(e.square)) return { text: `${who(c, e.player)} comprou o resort ${c.square(e.square)}`, tone: 'neutral' };
      return { text: `${who(c, e.player)} ${builtPhrase(e.from, e.level)} ${c.square(e.square)}`, tone: e.level === 4 ? 'hot' : 'neutral' };
    case 'takeover':
      return { text: `${who(c, e.buyer)} tomou ${c.square(e.square)} de ${c.name(e.from)} por ${m(e.price)}`, tone: 'bad' };
    case 'rentPaid':
      return {
        text: `${who(c, e.payer)} pagou ${m(e.amount)} de aluguel para ${c.name(e.owner)} em ${c.square(e.square)}`,
        tone: e.facada ? 'bad' : 'neutral',
      };
    case 'rentWaived': {
      const why: Record<string, string> = {
        interditada: 'está interditada pelo fiscal',
        apagao: 'é dia de apagão',
        calote: 'deu calote',
      };
      return { text: `${who(c, e.payer)} não pagou nada em ${c.square(e.square)}: ${why[e.reason] ?? e.reason}`, tone: 'good' };
    }
    case 'paid':
      if (e.reason === 'aluguel' || e.reason === 'construcao' || e.reason === 'compra' || e.reason === 'tomada') return null;
      return {
        text: `${who(c, e.from)} pagou ${m(e.amount)} ${e.to === 'bank' ? 'ao banco' : `para ${c.name(e.to)}`}${
          CARD_TEXT[e.reason] ? ` (${CARD_TEXT[e.reason]!.title})` : reasonText[e.reason] ? ` ${reasonText[e.reason]}` : ''
        }`,
        tone: 'bad',
      };
    case 'received':
      if (e.reason === 'inicio' || e.reason === 'venda') return null;
      return { text: `${who(c, e.player)} recebeu ${m(e.amount)}`, tone: 'good' };
    case 'debtOpened':
      return { text: `${who(c, e.debtor)} está devendo ${m(e.amount)}`, tone: 'bad' };
    case 'propertySold':
      return { text: `${who(c, e.player)} vendeu ${c.square(e.square)} ao banco por ${m(e.amount)}`, tone: 'bad' };
    case 'bankrupt':
      return { text: `${who(c, e.player)} faliu e está fora da partida`, tone: 'bad' };
    case 'groupCompleted':
      return { text: `${who(c, e.player)} fechou um grupo de cor: aluguel em dobro`, tone: 'hot' };
    case 'championship':
      return { text: `Campeonato em ${c.square(e.square)}: aluguel x${e.mult}`, tone: 'hot' };
    case 'worldTourReady':
      return { text: `${who(c, e.player)} vai voar no ${c.square(24)} na próxima vez`, tone: 'neutral' };
    case 'traveled':
      return { text: `${who(c, e.player)} voou para ${c.square(e.to)}`, tone: 'neutral' };
    case 'cardDrawn':
      return { text: `${who(c, e.player)} puxou "${CARD_TEXT[e.card]?.title ?? e.card}"`, tone: 'hot' };
    case 'cardKept':
      return { text: `${who(c, e.player)} guardou ${HELD_TEXT[e.card] ?? e.card}`, tone: 'neutral' };
    case 'cardDiscardedHandFull':
      return { text: `Mão cheia: a carta voltou para o baralho`, tone: 'neutral' };
    case 'cardUsed':
      return { text: `${who(c, e.player)} usou ${HELD_TEXT[e.card] ?? e.card}`, tone: 'neutral' };
    case 'cardNoEffect':
      return { text: `A carta não teve efeito desta vez`, tone: 'neutral' };
    case 'cardLost':
      return { text: `${who(c, e.player)} perdeu ${HELD_TEXT[e.card] ?? e.card}`, tone: 'bad' };
    case 'cardStolen':
      return { text: `${who(c, e.to)} roubou ${HELD_TEXT[e.card] ?? e.card} de ${c.name(e.from)}`, tone: 'bad' };
    case 'houseDestroyed':
      return { text: `${e.by ? `${who(c, e.by)} derrubou` : 'Caiu'} uma casa em ${c.square(e.square)}`, tone: 'bad' };
    case 'shieldBlocked':
      return { text: `O Seguro Anti-Zoeira de ${c.name(e.target)} bloqueou o ataque`, tone: 'good' };
    case 'interdicted':
      return { text: `${c.square(e.square)} foi interditada`, tone: 'bad' };
    case 'swapped':
      return { text: `${who(c, e.a)} trocou de lugar com ${c.name(e.b)}`, tone: 'hot' };
    case 'forcedPurchase':
      return { text: `${who(c, e.buyer)} comprou à força ${c.square(e.square)} de ${c.name(e.from)} por ${m(e.price)}`, tone: 'bad' };
    case 'tensionChanged':
      return { text: `Tensão: ${TENSION[e.level].name}`, tone: 'hot' };
    case 'caosAdded':
      return { text: 'As cartas de Caos entraram no baralho', tone: 'hot' };
    case 'finalRoundStarted':
      return { text: 'Última rodada!', tone: 'hot' };
    case 'gameEnded':
      return { text: 'Fim de jogo', tone: 'hot' };
    default:
      return null;
  }
}

/** Eventos que merecem tomar a tela por um instante. */
export function momentOf(e: GameEvent): Moment | null {
  switch (e.type) {
    case 'cardDrawn':
      return { kind: 'card', player: e.player, card: e.card, clover: !!e.clover };
    case 'rentPaid':
      return e.facada
        ? { kind: 'facada', payer: e.payer, owner: e.owner, amount: e.amount, square: e.square, mortal: e.mortal }
        : null;
    case 'groupCompleted':
      return { kind: 'group', player: e.player, group: e.group };
    case 'hotelBuilt':
      return e.firstOfGame ? { kind: 'hotel', player: e.player, square: e.square } : null;
    case 'bankrupt':
      return { kind: 'bankrupt', player: e.player };
    case 'takeover':
      return { kind: 'takeover', buyer: e.buyer, from: e.from, square: e.square, price: e.price };
    case 'houseDestroyed':
      return { kind: 'destroyed', owner: e.owner, square: e.square, by: e.by };
    case 'shieldBlocked':
      return { kind: 'shield', target: e.target };
    case 'taxEvasion':
      return e.success
        ? { kind: 'banner', title: 'Sonegou!', line: `Tirou ${e.total} nos dados e pagou só 20% do imposto`, tone: 'hot' }
        : { kind: 'banner', title: 'Pego na malha fina', line: `Tirou ${e.total}: paga o dobro e vai preso por 3 rodadas`, tone: 'bad' };
    case 'finalRoundStarted':
      return { kind: 'banner', title: 'Última rodada', line: 'Todo mundo joga mais uma vez e acabou', tone: 'hot' };
    case 'caosAdded':
      return { kind: 'banner', title: 'Caos liberado', line: 'Terremoto, apagão e companhia entraram no baralho', tone: 'bad' };
    case 'tensionChanged':
      return e.level >= 2 ? { kind: 'banner', title: TENSION[e.level].name, line: TENSION[e.level].line, tone: e.level >= 3 ? 'bad' : 'hot' } : null;
    default:
      return null;
  }
}
